/**
 * Interface languages and the rules for choosing one.
 *
 * Pure functions with no browser or Vite dependency, so they run under
 * `node --test` as well as in the app.
 *
 * WHO DECIDES THE LANGUAGE
 *
 *   1. the person's own choice      iam.users.preferred_language
 *   2. their organization's default platform.tenant_settings.default_language
 *   3. the system default           English
 *
 * The same order applies to the timezone (users.preferred_timezone, then the
 * organization's timezone). Both are stored server-side, so a choice follows
 * the person to another browser; localStorage only remembers the last
 * language so the sign-in screen can open in it.
 *
 * ADDING A LANGUAGE: add it here, to SUPPORTED_LANGUAGES in ERP-BACKEND
 * packages/shared/src/schemas.js, and create src/i18n/locales/<code>/ with
 * every namespace. See ERP-BACKEND/docs/LOCALIZATION.md.
 */

export const LANGUAGES = Object.freeze([
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
]);

/** The language every key is written in first, and the last fallback. */
export const SOURCE_LANGUAGE = 'en';
export const DEFAULT_LANGUAGE = 'en';

/**
 * Translation namespaces — one JSON file per domain per language. `common`
 * and `navigation` are needed by the shell on every screen and load up front;
 * the rest load the first time a screen asks for them.
 */
export const NAMESPACES = Object.freeze([
  'common',
  'navigation',
  'administration',
  'localization',
  'purchase',
  'rfq',
  'suppliers',
  'inventory',
  'sales',
  'accounting',
  'crm',
  'hr',
  'hrSelfService',
  'masterData',
  'analytics',
  'portal',
]);
export const EAGER_NAMESPACES = Object.freeze(['common', 'navigation']);

const BY_CODE = new Map(LANGUAGES.map((language) => [language.code, language]));

export const isSupportedLanguage = (code) => BY_CODE.has(code);

/** "ar-AE" -> "ar"; anything unsupported -> null. */
export function normaliseLanguage(code) {
  if (!code) return null;
  const base = String(code).trim().toLowerCase().split(/[-_]/)[0];
  return isSupportedLanguage(base) ? base : null;
}

export const directionOf = (code) => BY_CODE.get(normaliseLanguage(code))?.dir ?? 'ltr';
export const isRtlLanguage = (code) => directionOf(code) === 'rtl';

/**
 * The language shown beside the primary one in bilingual mode: Arabic beside
 * English, and English beside Arabic.
 */
export const secondaryLanguageFor = (primary) => (normaliseLanguage(primary) === 'ar' ? 'en' : 'ar');

/** User choice -> organization default -> system default. */
export function resolveLanguage({ userLanguage, organizationLanguage } = {}) {
  return normaliseLanguage(userLanguage) ?? normaliseLanguage(organizationLanguage) ?? DEFAULT_LANGUAGE;
}

/** True when `zone` is an IANA zone this browser can format in. */
export function isValidTimeZone(zone) {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * User choice -> organization timezone -> the browser's own zone. The last
 * step only matters before an organization is open (sign-in, public portals).
 */
export function resolveTimeZone({ userTimeZone, organizationTimeZone } = {}) {
  if (isValidTimeZone(userTimeZone)) return userTimeZone;
  if (isValidTimeZone(organizationTimeZone)) return organizationTimeZone;
  return undefined;
}
